import { CUSTOM_VIEW, isCustomViewAvailable } from '@src/constants/editor';
import { MilkdownEditor } from '@src/components/milkdown-editor/MilkdownEditor';
import { SnippetView } from './SnippetView';
import { ensureShikiMonaco, monacoTheme } from './shikiMonaco';
import { copyToClipboard, downloadFile, EditorLanguage, /* FSEntry, */ fsStore, isImagePath, VFSPath } from '@sdk';
import { useContext, useProject } from '@sdk/react/hooks';
import { Button } from '@src/components/ui/button';
import { useFS } from '@src/hooks/useFS';
import Editor, { Monaco } from '@monaco-editor/react';
import { Copy, Download, Eye, Play, /* PlayCircle, */ RefreshCw } from 'lucide-react';
import { editor } from 'monaco-editor';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';

const SAVE_TIMEOUT = 1000; // 1 second

const LANGUAGE_COMMANDS = {
  python: 'python',
  javascript: 'bun',
  typescript: 'bun',
  shell: '',
} as const;

const isExecutableScript = (language: string) => {
  return language in LANGUAGE_COMMANDS;
};

/** Only picks the default view; what a region IS is decided by the backend. */
const SNIPPET_MARKER = /^\s*(?:#|\/\/|--)\s*%%\s*flowpad:(?:hidden|init|snippet)\b/m;

/**
 * TODO: How do we want to sync the editor changes into the agent if at all?
 *
 * Placeholder for notifying the agent about file changes.
 * This could be used to:
 * - Update agent context with file changes
 * - Keep agent state in sync with file system
 *
 * @param operation - The type of operation (e.g., 'write', 'delete')
 * @param path - The file path that changed
 * @param content - The new content (for write operations)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const notifyAgentOnChange = (_operation: string, _path: string, _content?: string) => {
  // Empty placeholder - design TBD
};

export interface EditorFileData {
  path: string;
  content?: string;
  blob?: Blob;
  language: EditorLanguage;
}

interface EditorPaneProps {
  file?: EditorFileData;
  readOnly?: boolean;
  /**
   * 1-indexed line to reveal on open, from the dock's `?line=` option. A deep
   * link into a file (e.g. an interface block's "Open in editor") lands here.
   */
  revealLine?: number | null;
  revealColumn?: number | null;
  onExecuteScript?: () => void;
  onShellCmd?: (command: string) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const EditorPane: React.FC<EditorPaneProps> = ({
  file,
  readOnly,
  revealLine,
  revealColumn,
  onExecuteScript,
  onShellCmd,
  onDirtyChange,
}) => {
  const { t } = useLingui();
  const { agenticProcess } = useContext();
  const { project } = useProject();
  // const { navigation } = useDockNavigation();

  // Parse file path to extract typeId (if present) for cross-context file loading
  const parsedFilePath = useMemo(() => VFSPath.parse(file?.path), [file?.path]);
  // Use typeId from path if available, otherwise fall back to project's typeId
  const effectiveTypeId = useMemo(
    () => parsedFilePath.typeId ?? project?.typeId,
    [parsedFilePath.typeId, project?.typeId],
  );
  // The actual file path within the entity (without typeId prefix)
  const effectiveFilePath = useMemo(() => {
    if (!parsedFilePath.typeId) return file?.path;
    const subPath = parsedFilePath.entitySubPath;
    return subPath.startsWith('/') ? subPath : `/${subPath}`;
  }, [parsedFilePath.typeId, parsedFilePath.entitySubPath, file?.path]);

  const fs = useFS(effectiveTypeId);

  // Images are binary: render them inline via the backend download URL instead
  // of decoding the bytes as text into Monaco (which shows garbage).
  const isImage = useMemo(() => isImagePath(effectiveFilePath || file?.path || ''), [effectiveFilePath, file?.path]);
  const imageUrl = useMemo(
    () => (isImage && effectiveFilePath && fs ? fs.getDownloadUrl(effectiveFilePath) : null),
    [isImage, effectiveFilePath, fs],
  );

  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  // `Monaco` is already `any` in @monaco-editor/react's types, so `| null`
  // added nothing to the union.
  const monacoRef = useRef<Monaco>(null);
  const { resolvedTheme } = useTheme();
  const [isExecuting, setIsExecuting] = useState(false);
  const [isCustomView, setIsCustomView] = useState(isCustomViewAvailable(file?.language || ''));
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Sync custom-view default when the file (or its language) changes
  useEffect(() => {
    setIsCustomView(isCustomViewAvailable(file?.language || ''));
  }, [file?.path, file?.language]);
  const [notSnippet, setNotSnippet] = useState(false);
  useEffect(() => setNotSnippet(false), [file?.path]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingExecuteRef = useRef<null | { language: string; filePath: string }>(null);

  // Get content from FSStore (reactive) - NO fallback to props
  // History is completely decoupled from editor
  // Use effectiveFilePath for FS operations (path without typeId prefix)
  const cached = effectiveFilePath && effectiveTypeId ? fs?.content(effectiveFilePath) : null;
  const fileContent = (cached?.content as string) || '';
  const isDirty = cached?.isDirty || false;

  // A snippet file opens in the snippet view (markers → regions + Run); the Eye
  // toggle goes to the raw file. The backend needs the machine path, which a
  // local file's VFS path carries directly and a project file joins to the mount.
  // Decided only from loaded content: while the file is (re)fetching `fileContent`
  // is '' and a snippet would flicker to "not a snippet", unmounting its editors.
  const [isSnippet, setIsSnippet] = useState(false);
  useEffect(() => setIsSnippet(false), [file?.path]);
  useEffect(() => {
    if (cached) setIsSnippet(!notSnippet && SNIPPET_MARKER.test(fileContent));
  }, [cached, fileContent, notSnippet]);
  const snippetPath = useMemo(() => {
    if (parsedFilePath.type === 'compute_node') return parsedFilePath.machinePath;
    const mount = project?.fs_storage_mount_path;
    return mount && effectiveFilePath ? `${mount.replace(/\/+$/, '')}/${effectiveFilePath.replace(/^\/+/, '')}` : '';
  }, [parsedFilePath, project?.fs_storage_mount_path, effectiveFilePath]);
  const snippetDefaultedRef = useRef<string | null>(null);
  useEffect(() => {
    if (isSnippet && snippetDefaultedRef.current !== file?.path) {
      snippetDefaultedRef.current = file?.path ?? null;
      setIsCustomView(true);
    }
  }, [isSnippet, file?.path]);
  // Auto-download file content if not in cache
  useEffect(() => {
    if (!effectiveFilePath || !effectiveTypeId) return;

    // Images are streamed straight from the download URL into an <img>; never
    // pull their bytes as text.
    if (isImage) return;

    // Check if content is already cached
    if (cached) {
      return;
    }

    // Download file content from server
    const downloadContent = async () => {
      if (!fs) return;
      try {
        await fs.download(effectiveFilePath, false);
      } catch (error) {
        console.error('[EditorPane] ❌ Error downloading content:', effectiveFilePath, error);
      }
    };

    void downloadContent();
  }, [effectiveFilePath, effectiveTypeId, cached, fs]);

  const setIsDirty = useCallback(
    (dirty: boolean) => {
      onDirtyChange?.(dirty);
    },
    [onDirtyChange],
  );

  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const userScrollRef = useRef(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const editorDomNode = editor.getDomNode();
    if (!editorDomNode) return;

    let lastUserScrollTime = 0;

    const markUserScroll = () => {
      lastUserScrollTime = Date.now();
    };

    const isEditorAtBottom = () => {
      const scrollTop = editor.getScrollTop();
      const scrollHeight = editor.getScrollHeight();
      const clientHeight = editor.getDomNode()?.clientHeight ?? 0;
      return scrollTop + clientHeight >= scrollHeight - 10;
    };

    editorDomNode.addEventListener('wheel', markUserScroll);
    editorDomNode.addEventListener('pointerdown', markUserScroll);
    editorDomNode.addEventListener('touchstart', markUserScroll);

    const scrollDisposable = editor.onDidScrollChange(() => {
      const timeSince = Date.now() - lastUserScrollTime;

      if (timeSince < 150 && !isEditorAtBottom()) {
        userScrollRef.current = true;
        setIsUserScrolling(true);
        return;
      }

      if (isEditorAtBottom() && userScrollRef.current) {
        userScrollRef.current = false;
        setIsUserScrolling(false);
      }
    });

    return () => {
      scrollDisposable.dispose();
      editorDomNode.removeEventListener('wheel', markUserScroll);
      editorDomNode.removeEventListener('pointerdown', markUserScroll);
      editorDomNode.removeEventListener('touchstart', markUserScroll);
    };
  }, [file]);

  // A deep link is honoured once per (file, line): re-revealing on every content
  // change would fight the user as they scroll away from it.
  const revealedRef = useRef<string | null>(null);
  // The file whose first content already landed. Opening a file shows its top,
  // like any code editor; only content arriving AFTER that (an agent streaming
  // the file) is tail-followed.
  const openedRef = useRef<string | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const [editorReady, setEditorReady] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const model = editor.getModel();
    if (!model) return;

    // An explicit line wins over the tail-follow below. Without this the
    // `revealLine(getLineCount())` call would scroll a deep link straight to
    // the bottom of the file the moment content settled.
    if (revealLine && revealLine > 0) {
      const key = `${file?.path ?? ''}#${revealLine}:${revealColumn ?? 1}`;
      if (revealedRef.current === key) return;
      const line = Math.min(revealLine, model.getLineCount());
      editor.revealLineInCenter(line);
      const column = Math.max(1, Math.min(revealColumn ?? 1, model.getLineMaxColumn(line)));
      editor.setPosition({ lineNumber: line, column });
      // Scrolling there isn't enough to SEE it in a wall of code — mark the
      // line until the next deep link replaces the decoration.
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, [
        {
          range: { startLineNumber: line, startColumn: 1, endLineNumber: line, endColumn: 1 },
          options: { isWholeLine: true, className: 'flowpad-deep-link-line' },
        },
      ]);
      revealedRef.current = key;
      return;
    }

    const path = file?.path ?? '';
    if (openedRef.current !== path) {
      if (fileContent) openedRef.current = path;
      return;
    }
    if (isUserScrolling) return;
    editor.revealLine(model.getLineCount());
    /*
     * Depends on `fileContent`, not `file.content`: this pane sources content
     * from the FS cache, so the prop stays undefined and an effect keyed on it
     * never re-runs after the text arrives. `editorReady` covers the other half
     * — the first run happens before Monaco mounts and bails on the null ref.
     * Missing either one is why a deep link silently landed on line 1.
     */
  }, [file?.path, fileContent, isUserScrolling, revealLine, revealColumn, editorReady]);

  const clearSaveTimeout = useCallback(() => {
    if (!saveTimeoutRef.current) return;
    clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = null;
  }, []);

  const executeScript = useCallback(
    (language: string, filePath: string) => {
      if (!agenticProcess) return;

      setIsExecuting(true);

      try {
        onExecuteScript?.();

        const command = LANGUAGE_COMMANDS[language as keyof typeof LANGUAGE_COMMANDS] || '';
        const shellCommand = command ? `${command} ${filePath}` : filePath.includes('/') ? filePath : `./${filePath}`;

        // Notify parent about shell command
        onShellCmd?.(shellCommand);
      } catch (error) {
        console.error('Error executing script:', error);
      } finally {
        setIsExecuting(false);
      }
    },
    [agenticProcess, onExecuteScript, onShellCmd],
  );

  const handleEditorDidMount = (editor: editor.IStandaloneCodeEditor, monaco: Monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    // A ref alone can't retrigger the reveal effect below, which runs (and
    // bails) before Monaco exists.
    setEditorReady(true);

    if (!file) return;

    async function setupEditor(language: EditorLanguage) {
      await ensureShikiMonaco(monaco, language);
      editor.updateOptions({
        fontSize: 14,
        lineHeight: 20,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        wordWrap: 'on',
        theme: monacoTheme(resolvedTheme),
        readOnly: readOnly,
      });
    }
    void setupEditor(file.language);
  };

  // No manual dispose here: <Editor> disposes its model and then its editor on
  // unmount. A parent-side `editorRef.current.dispose()` ran BEFORE that cleanup
  // (React runs the parent's effect cleanup first) and detached the model
  // without disposing it, so the library's `getModel()?.dispose()` saw null —
  // one orphan model holding the whole file text per unmount (B8).

  // Stable onChange handler — uses a ref so MilkdownEditor's useEditor (which
  // depends on [onChange]) doesn't re-initialize and lose focus on every keystroke.
  const handleContentChangeRef = useRef<(value: string | undefined) => void>();
  handleContentChangeRef.current = (value: string | undefined) => {
    if (value === undefined || !file || !effectiveFilePath || value === fileContent) {
      return;
    }

    clearSaveTimeout();

    // Update FSStore with new content (mark as dirty for local edits)
    if (!fs) return;
    fs.setContent(effectiveFilePath, value, true);
    setIsDirty(true);

    // Auto-save after SAVE_TIMEOUT of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      void handleSaveFile();
    }, SAVE_TIMEOUT);
  };
  const handleContentChange = useCallback((value: string | undefined) => {
    handleContentChangeRef.current?.(value);
  }, []);

  const handleExecuteScript = (language: string, filePath: string) => {
    if (isDirty) {
      pendingExecuteRef.current = { language, filePath };
      return;
    }
    void executeScript(language, filePath);
  };

  const handleRefreshFile = useCallback(async () => {
    if (!agenticProcess || !file || !fs || !effectiveFilePath) return;

    setIsRefreshing(true);
    try {
      // Invalidate cache to force re-fetch
      fs.invalidate(effectiveFilePath, 'content');

      // Download fresh content from server
      await fs.download(effectiveFilePath, false);
    } catch (error) {
      console.error('[EditorPane] Error refreshing file:', effectiveFilePath, error);
    } finally {
      setIsRefreshing(false);
    }
  }, [file, agenticProcess, fs, effectiveFilePath]);

  // The snippet view reads/writes the file through the backend and hands back its
  // text, set here as clean content so a stale raw copy can't undo a snippet edit
  // from the Eye view. Not invalidate-and-refetch: that blanks `fileContent` and
  // unmounts the snippet view mid-typing. Unsaved raw edits are never overwritten.
  // Read through a ref so the callback stays stable across content changes.
  const rawRef = useRef({ isDirty, fileContent });
  rawRef.current = { isDirty, fileContent };
  const adoptSnippetText = useCallback(
    (text: string) => {
      const raw = rawRef.current;
      if (!fs || !effectiveFilePath || raw.isDirty || text === raw.fileContent) return;
      fs.setContent(effectiveFilePath, text, false);
    },
    [fs, effectiveFilePath],
  );

  // Navigate to execute-flow page with current file
  // const handleExecuteFlow = useCallback(() => {
  //   if (!file || !effectiveTypeId || !effectiveFilePath) return;
  //
  //   // Create FSEntry for the current file
  //   // Use effectiveTypeId and effectiveFilePath for cross-context file support
  //   const fsItem = new FSEntry({
  //     is_dir: false,
  //     vfs_abs_path: `${effectiveTypeId.toString()}${effectiveFilePath}`,
  //     size: 0,
  //   });
  //
  //   // Navigate to execute-flow page with the FSEntry
  //   navigation.openExecuteFlow({ file: fsItem });
  // }, [file, effectiveTypeId, effectiveFilePath, navigation]);

  const handleSaveFile: () => Promise<void> = useCallback(async () => {
    if (!file || !effectiveTypeId || !effectiveFilePath) {
      return;
    }

    // Read isDirty directly from Zustand store to avoid stale selector
    // Use effectiveFilePath for cache key construction
    const normalizedPath = effectiveFilePath.replace(/\/+/g, '/').replace(/\/$/, '') || '/';
    const textKey = `${effectiveTypeId.toString()}:${normalizedPath}:text`;
    const currentCached = fsStore.getState().contentCache.get(textKey);
    const currentIsDirty = currentCached?.isDirty || false;
    const currentContent = (currentCached?.content as string) || editorRef.current?.getValue() || '';

    const onFileSaved = () => {
      setIsDirty(false);

      if (!pendingExecuteRef.current) return;
      const { language, filePath } = pendingExecuteRef.current;
      pendingExecuteRef.current = null;
      void executeScript(language, filePath);
    };

    if (!currentContent) {
      console.error('[EditorPane] 💾 Editor content not available');
      return;
    }

    // Only save if dirty (check from FSStore, not closure)
    if (!currentIsDirty) {
      clearSaveTimeout();
      onFileSaved();
      return;
    }

    if (!fs) {
      console.error('[EditorPane] 💾 Cannot save - fs is null');
      return;
    }

    try {
      // Write back to server via REST API
      await fs.writeBack(effectiveFilePath);

      // Mark as saved (writeBack already calls markClean internally)
      onFileSaved();

      // Notify agent about the change (placeholder for future design)
      notifyAgentOnChange('write', effectiveFilePath, currentContent);
    } catch (error) {
      console.error('[EditorPane] 💾 Error saving file:', effectiveFilePath, error);
      // Keep asterisk on if save fails
    }
  }, [file, fs, setIsDirty, executeScript, clearSaveTimeout, effectiveTypeId, effectiveFilePath]);

  if (!file) {
    return (
      <div className="flex flex-1 items-center justify-center text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </div>
    );
  }

  // Image files render inline rather than in Monaco.
  if (isImage) {
    return (
      <div className="group relative flex h-full min-h-0 flex-1 items-center justify-center overflow-auto bg-muted/20 p-4">
        {imageUrl ? (
          <>
            <img
              src={imageUrl}
              alt={file.path.split('/').pop() || file.path}
              className="max-h-full max-w-full object-contain"
            />
            <a
              href={imageUrl}
              download={file.path.split('/').pop() || file.path}
              title={t`Download`}
              className="absolute right-5 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-lg border bg-background/50 text-foreground opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
            >
              <Download className="h-4 w-4" />
            </a>
          </>
        ) : (
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
      </div>
    );
  }

  return (
    <div className="group relative flex h-full min-h-0 flex-1 flex-col">
      <div className="editor-pane-actions absolute right-5 top-2 z-10 flex flex-row-reverse rounded-lg border bg-background/50 group-hover:gap-2">
        <div>
          {/* <Button
            variant="ghost"
            size="icon"
            onClick={handleExecuteFlow}
            title={t`Execute in Flow`}
            className="hover:bg-muted"
          >
            <PlayCircle className="h-4 w-4" />
          </Button> */}
          {isExecutableScript(file.language) && !(isCustomView && isSnippet) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleExecuteScript(file.language, file.path)}
              title={t`Run script`}
              className="hover:bg-muted"
              disabled={isExecuting}
            >
              {isExecuting ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </Button>
          )}
          {(isCustomViewAvailable(file.language) || isSnippet) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCustomView((prev) => !prev)}
              title={t`Toggle view`}
              className="hover:bg-muted"
            >
              <Eye className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="w-0 origin-right overflow-hidden transition-all group-hover:w-28">
          <div className="flex w-28">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void handleRefreshFile()}
              title={t`Refresh file from server`}
              className="hover:bg-muted"
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void copyToClipboard(fileContent)}
              title={t`Copy`}
              className="hover:bg-muted"
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => downloadFile({ name: file.path, content: file.blob || new Blob([fileContent]) })}
              title={t`Download`}
              className="hover:bg-muted"
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="h-full" style={{ position: 'relative' }}>
        {isCustomView && isSnippet && snippetPath ? (
          <SnippetView
            path={snippetPath}
            watch={effectiveTypeId && effectiveFilePath ? { typeid: effectiveTypeId, path: effectiveFilePath } : undefined}
            language={file.language}
            revision={fileContent}
            readOnly={readOnly}
            onNotSnippet={() => setNotSnippet(true)}
            onSynced={adoptSnippetText}
          />
        ) : isCustomView && isCustomViewAvailable(file.language) ? (
          CUSTOM_VIEW[file.language as keyof typeof CUSTOM_VIEW] === 'markdown' ? (
            <div className="h-full w-full overflow-auto">
              <MilkdownEditor
                content={fileContent}
                onChange={readOnly ? undefined : handleContentChange}
                editorMode={readOnly ? 'view' : 'editor'}
              />
            </div>
          ) : CUSTOM_VIEW[file.language as keyof typeof CUSTOM_VIEW] === 'html' ? (
            <iframe
              className="h-full w-full border-0"
              srcDoc={fileContent}
              sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups allow-popups-to-escape-sandbox"
              title={t`HTML Preview`}
            />
          ) : null
        ) : (
          <Editor
            height="100%"
            language={file.language}
            value={fileContent}
            onChange={handleContentChange}
            onMount={handleEditorDidMount}
            theme={monacoTheme(resolvedTheme)}
            options={{
              readOnly: readOnly,
              fontSize: 14,
              lineHeight: 20,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              wordWrap: 'on',
              padding: { top: 16, bottom: 16 },
              lineNumbers: 'on',
              glyphMargin: true,
              folding: true,
              renderLineHighlight: 'all',
              selectOnLineNumbers: true,
            }}
          />
        )}
      </div>
    </div>
  );
};
